import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, LANGS } from "@/i18n/LanguageContext";
import { useAmbience } from "@/context/AmbienceContext";
import { FRAMES } from "@/components/spa/ArrivalJourney";
import { IMAGES } from "@/data/spa";
import { Magnetic } from "@/components/spa/Magnetic";

// Cinematic imagery that reveals during the count-up 0 -> 100
const REVEAL_IMAGES = [
  IMAGES.zenStones,
  IMAGES.hotStone,
  IMAGES.headMassage,
  IMAGES.footMassage,
  "/spa/gift-certificate.jpg",
];

const STEAM = Array.from({ length: 8 }, (_, i) => ({
  left: `${8 + i * 11}%`,
  size: 90 + ((i * 47) % 100),
  duration: 12 + (i % 4) * 2,
  delay: i * 1.0,
  drift: i % 2 ? "60px" : "-50px",
}));

const LOAD_MS = 2600;
const BOW_MS = 3400;

export const ArrivalGate = () => {
  const { t, lang, setLang } = useLang();
  const { start } = useAmbience();
  const [open, setOpen] = useState(true);
  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading -> welcome
  const [revealIdx, setRevealIdx] = useState(0);
  const [bowDone, setBowDone] = useState(false);
  const videoRef = useRef(null);

  // Preload arrival poster frames + reveal images in the background
  useEffect(() => {
    [...FRAMES.map((f) => f.poster), ...REVEAL_IMAGES, "/welcome/v6.jpg"].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Smooth 0 -> 100 count-up animation using rAF, tied to LOAD_MS
  useEffect(() => {
    if (phase !== "loading") return;
    const startTime = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / LOAD_MS);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setCount(Math.round(eased * 100));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        // Small pause at 100, then move to welcome
        setTimeout(() => setPhase("welcome"), 350);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  // Rotate the reveal imagery every 500ms during loading
  useEffect(() => {
    if (phase !== "loading") return;
    const iv = setInterval(() => setRevealIdx((i) => (i + 1) % REVEAL_IMAGES.length), 550);
    return () => clearInterval(iv);
  }, [phase]);

  // Once welcome phase begins, play V6 and mark bow complete after ~3.4s
  useEffect(() => {
    if (phase !== "welcome") return;
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
    const timer = setTimeout(() => setBowDone(true), BOW_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const enter = () => {
    start();
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="arrival-gate"
          exit={{ opacity: 0, transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] } }}
          className="fixed inset-0 z-[120] bg-[#050706] overflow-hidden"
        >
          {/* PHASE 1 — Loading with reveal imagery + 01→100 counter */}
          <AnimatePresence>
            {phase === "loading" && (
              <motion.div
                key="loading"
                exit={{ opacity: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } }}
                className="absolute inset-0"
                data-testid="gate-loading"
              >
                {/* Rotating spa imagery — very low opacity, blurred, epic */}
                <div className="absolute inset-0">
                  {REVEAL_IMAGES.map((src, i) => (
                    <motion.img
                      key={src}
                      src={src}
                      alt=""
                      aria-hidden="true"
                      animate={{
                        opacity: revealIdx === i ? 0.32 : 0,
                        scale: revealIdx === i ? 1.05 : 1.12,
                      }}
                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 w-full h-full object-cover blur-[6px]"
                    />
                  ))}
                  <div className="absolute inset-0 bg-gradient-to-b from-[#050706]/70 via-[#050706]/60 to-[#050706]/95" />
                </div>

                {/* Steam particles */}
                <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                  {STEAM.map((p, i) => (
                    <span
                      key={i}
                      className="steam-particle"
                      style={{
                        left: p.left,
                        width: p.size,
                        height: p.size,
                        animationDuration: `${p.duration}s`,
                        animationDelay: `${p.delay}s`,
                        "--steam-opacity": 0.1,
                        "--steam-drift": p.drift,
                      }}
                    />
                  ))}
                </div>

                {/* Breathing 靜 background */}
                <motion.span
                  aria-hidden="true"
                  animate={{ scale: [1, 1.08, 1], opacity: [0.05, 0.10, 0.05] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0 flex items-center justify-center font-serif-display text-[26rem] leading-none text-[#c5a059] select-none pointer-events-none"
                >
                  靜
                </motion.span>

                {/* Elite Spa serif with letter-spacing reveal */}
                <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center"
                  >
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.8 }}
                      className="text-[10px] tracking-[0.65em] uppercase text-[#c5a059] mb-6"
                    >
                      Elite Spa · Lakewood
                    </motion.p>
                    <motion.h1
                      initial={{ letterSpacing: "0.4em", opacity: 0 }}
                      animate={{ letterSpacing: "0.02em", opacity: 1 }}
                      transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
                      className="font-serif-display font-medium text-6xl sm:text-7xl lg:text-8xl text-[#f0f4f2]"
                    >
                      Elite <span className="italic gold-shimmer-text">Spa</span>
                    </motion.h1>

                    {/* 0 -> 100 counter in serif */}
                    <div className="mt-14 flex items-baseline justify-center gap-2" aria-live="polite">
                      <motion.span
                        key={count}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                        data-testid="gate-count"
                        className="font-serif-display italic text-[#c5a059] text-5xl sm:text-6xl leading-none tabular-nums"
                      >
                        {String(count).padStart(2, "0")}
                      </motion.span>
                      <span className="font-serif-display text-[#c5a059]/50 text-xl">/100</span>
                    </div>

                    {/* Gold progress line */}
                    <div className="mt-10 w-72 mx-auto h-px bg-white/10 overflow-hidden">
                      <div
                        className="block h-full bg-[#c5a059] origin-left"
                        style={{ transform: `scaleX(${count / 100})`, transition: "transform 90ms linear" }}
                      />
                    </div>

                    {/* Preparing text */}
                    <p className="mt-8 text-[10px] tracking-[0.45em] uppercase text-[#f0f4f2]/50">
                      {t.gate.preparing}
                    </p>
                  </motion.div>
                </div>

                {/* Language toggle top-right */}
                <div className="absolute top-6 right-6 flex gap-1">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`gate-lang-${l.code}`}
                      onClick={() => setLang(l.code)}
                      className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase border backdrop-blur-sm transition-colors duration-300 ${
                        lang === l.code
                          ? "border-[#c5a059] text-[#c5a059] bg-[#050706]/40"
                          : "border-white/20 text-[#f0f4f2]/70 hover:text-[#f0f4f2] bg-[#050706]/20"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* PHASE 2 — Welcome with V6 + CTA */}
          {phase === "welcome" && (
            <>
              <motion.video
                ref={videoRef}
                src="/welcome/v6.mp4"
                poster="/welcome/v6.jpg"
                muted
                loop
                playsInline
                preload="auto"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-contain sm:object-cover bg-[#050706] block"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#050706]/35 via-transparent to-[#050706]/95 pointer-events-none" />

              {/* Steam still rising */}
              <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                {STEAM.slice(0, 5).map((p, i) => (
                  <span
                    key={i}
                    className="steam-particle"
                    style={{
                      left: p.left,
                      width: p.size,
                      height: p.size,
                      animationDuration: `${p.duration}s`,
                      animationDelay: `${p.delay}s`,
                      "--steam-opacity": 0.06,
                      "--steam-drift": p.drift,
                    }}
                  />
                ))}
              </div>

              {/* Language toggle stays */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.9 }}
                className="absolute top-6 right-6 flex gap-1"
              >
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`gate-lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase border backdrop-blur-sm transition-colors duration-300 ${
                      lang === l.code
                        ? "border-[#c5a059] text-[#c5a059] bg-[#050706]/40"
                        : "border-white/20 text-[#f0f4f2]/70 hover:text-[#f0f4f2] bg-[#050706]/20"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </motion.div>

              {/* Enter CTA (reveals after bow completes) */}
              <div className="absolute bottom-0 left-0 right-0 pb-14 sm:pb-16 flex flex-col items-center gap-5 px-6">
                <AnimatePresence>
                  {bowDone && (
                    <motion.div
                      key="cta"
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                      className="flex flex-col items-center gap-5"
                      data-testid="gate-cta"
                    >
                      <Magnetic strength={0.25}>
                        <motion.button
                          data-testid="gate-enter"
                          onClick={enter}
                          whileHover={{ y: -2 }}
                          className="group relative overflow-hidden bg-[#c5a059] text-[#050706] px-10 sm:px-14 py-4 sm:py-5 text-[11px] sm:text-xs tracking-[0.35em] uppercase font-medium"
                        >
                          <span className="relative z-10">{t.veil.enter}</span>
                          <span className="absolute inset-0 bg-[#dcc286] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                        </motion.button>
                      </Magnetic>
                      <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-[#f0f4f2]/60">
                        <span className="flex items-end gap-[3px] h-3" aria-hidden="true">
                          {[0, 1, 2, 3].map((i) => (
                            <span
                              key={i}
                              className="sound-bar w-[2px] bg-[#c5a059]/70 rounded-full"
                              style={{ animationDelay: `${i * 0.2}s` }}
                            />
                          ))}
                        </span>
                        <span>{t.veil.hint}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
